import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Container } from '@components/common/Container';
import { Button } from '@components/common/Button';
import { TextField } from '@components/common/TextField';
import { EVENTS } from '@constants/telemetryEvents';
import { apiFetchUrl, assertApiConfigured } from '@constants/apiConfig';
import { VISIT_PATH } from '@constants/publicSearch';
import { isPublicSiteViewSearch, withPublicSiteView } from '@constants/publicSiteView';
import { useTelemetry } from '@hooks/useTelemetry';
import {
  canSubmitContact,
  validateContactForm,
} from '@utils';
import { MAPS_LINK_URL } from '@utils/landingMapConfig';

import { ContactMap } from './ContactMap';

const CONTACT_INTRO =
  'Have a question about BHG? Contact Director Beth Perkins using the form below.';

/** Honeypot: hidden from users; bots fill it. Leave empty = human. Obscure name to avoid autofill. */
const HONEYPOT_FIELD_NAME = 'hp_leave_empty';

const ADDRESS = {
  name: 'Bethlehem Baptist Church',
  street: '8400 Poole Road',
  city: 'Knightdale, NC 27545',
};

type ContactTouched = { name: boolean; email: boolean; phone: boolean; message: boolean };

const INITIAL_CONTACT_TOUCHED: ContactTouched = {
  name: false,
  email: false,
  phone: false,
  message: false,
};

export function ContactSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const { track } = useTelemetry();
  const [searchParams] = useSearchParams();
  const viewingPublicSite = isPublicSiteViewSearch(searchParams.toString());
  const visitTo = withPublicSiteView(VISIT_PATH, viewingPublicSite);
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState<ContactTouched>(INITIAL_CONTACT_TOUCHED);
  const [focused, setFocused] = useState<ContactTouched>(INITIAL_CONTACT_TOUCHED);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setFieldTouched = (field: keyof ContactTouched) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    setFocused((prev) => ({ ...prev, [field]: false }));
  };

  const setFieldFocused = (field: keyof ContactTouched, value: boolean) => {
    setFocused((prev) => ({ ...prev, [field]: value }));
  };

  /** Sync autofilled/pasted values into state (browser autofill and some paste don't fire onChange). */
  const syncFormFromDOM = () => {
    const form = formRef.current;
    if (!form) return;
    const nameEl = form.elements.namedItem('name') as HTMLInputElement | null;
    const emailEl = form.elements.namedItem('email') as HTMLInputElement | null;
    const messageEl = form.elements.namedItem('message') as HTMLInputElement | HTMLTextAreaElement | null;
    const phoneEl = form.elements.namedItem('phone') as HTMLInputElement | null;
    if (nameEl?.value) setName((prev) => (prev !== nameEl.value ? nameEl.value : prev));
    if (emailEl?.value) setEmail((prev) => (prev !== emailEl.value ? emailEl.value : prev));
    if (messageEl?.value) setMessage((prev) => (prev !== messageEl.value ? messageEl.value : prev));
    if (phoneEl?.value) setPhone((prev) => (prev !== phoneEl.value ? phoneEl.value : prev));
  };

  useEffect(() => {
    const t = setTimeout(syncFormFromDOM, 300);
    return () => clearTimeout(t);
  }, []);

  const handleFormInput = () => {
    syncFormFromDOM();
  };

  const contactValidation = validateContactForm({ name, email, phone, message });
  const canSubmit = canSubmitContact({ name, email, phone, message });

  async function submitContactForm(honeypotValue: string): Promise<void> {
    assertApiConfigured();
    const response = await fetch(apiFetchUrl('/contact'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        email,
        phone: phone || undefined,
        message,
        honeypot: honeypotValue || undefined,
      }),
    });
    if (!response.ok) {
      const err: unknown = await response.json().catch(() => ({}));
      throw new Error(
        (err as { error?: { message?: string } })?.error?.message ??
          `Request failed (${response.status})`
      );
    }
  }

  const handleContactSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotValue = (form.elements.namedItem(HONEYPOT_FIELD_NAME) as HTMLInputElement)
      ?.value ?? '';
    if (typeof honeypotValue === 'string' && honeypotValue.trim().length > 0) return;
    if (!canSubmit) {
      setTouched({ name: true, email: true, phone: true, message: true });
      setFocused(INITIAL_CONTACT_TOUCHED);
      return;
    }
    setIsSubmitting(true);
    setContactStatus('idle');
    try {
      await submitContactForm(honeypotValue);
      setContactStatus('success');
      track(EVENTS.LANDING_CONTACT_FORM_SUBMITTED);
    } catch {
      setContactStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bhg-landing-section bhg-landing-contact">
      <Container size="lg">
        <div className="bhg-landing-contact__header">
          <h2 id="contact-heading" className="bhg-landing-section__title">Get in Touch</h2>
          <p className="bhg-landing-contact__intro">
            Have questions or want to visit? We&apos;d love to hear from you!
          </p>
          <Link
            to={visitTo}
            state={{ visitSource: 'contact-section' }}
            className="bhg-button bhg-button--outline bhg-button--md bhg-landing-contact__visit"
          >
            <span className="bhg-button__content">Schedule a Visit</span>
          </Link>
        </div>

        <div className="bhg-landing-contact__layout">
          <div className="bhg-landing-contact__form-wrapper">
            {contactStatus === 'success' ? (
              <div className="bhg-landing-contact__success" role="status" aria-live="polite">
                <h3 className="bhg-landing-contact__success-title">
                  Message sent
                </h3>
                <p className="bhg-landing-contact__success-text">
                  Thank you! Your message has been sent. We&apos;ll get back to you soon.
                </p>
                <Link
                  to={visitTo}
                  state={{ visitSource: 'contact-section' }}
                  className="bhg-button bhg-button--outline bhg-button--md bhg-landing-contact__success-action"
                >
                  <span className="bhg-button__content">Schedule a Visit</span>
                </Link>
              </div>
            ) : (
              <>
                <h3 className="bhg-landing-contact__form-title">
                  Contact the Director
                </h3>
                <p className="bhg-landing-contact__form-intro">{CONTACT_INTRO}</p>
                <form
                  ref={formRef}
                  className="bhg-landing-contact__form"
                  onSubmit={handleContactSubmit}
                  onInput={handleFormInput}
                  noValidate
                >
                  <TextField
                    label="Name"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => setFieldFocused('name', true)}
                    onBlur={() => setFieldTouched('name')}
                    required
                    autoComplete="name"
                    fullWidth
                    error={touched.name && !focused.name ? contactValidation.errors.name : undefined}
                  />
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFieldFocused('email', true)}
                    onBlur={() => setFieldTouched('email')}
                    required
                    autoComplete="email"
                    fullWidth
                    error={touched.email && !focused.email ? contactValidation.errors.email : undefined}
                  />
                  <TextField
                    label="Phone number"
                    name="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onFocus={() => setFieldFocused('phone', true)}
                    onBlur={() => setFieldTouched('phone')}
                    autoComplete="tel"
                    fullWidth
                    error={touched.phone && !focused.phone ? contactValidation.errors.phone : undefined}
                  />
                  <TextField
                    label="Message"
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onFocus={() => setFieldFocused('message', true)}
                    onBlur={() => setFieldTouched('message')}
                    multiline
                    rows={4}
                    required
                    fullWidth
                    error={touched.message && !focused.message ? contactValidation.errors.message : undefined}
                  />
                  <div className="bhg-landing-contact__honeypot" aria-hidden="true">
                    <label>
                      Leave this blank
                      <input
                        type="text"
                        name={HONEYPOT_FIELD_NAME}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden
                      />
                    </label>
                  </div>
                  {contactStatus === 'error' && (
                    <p className="bhg-landing-contact__message bhg-landing-contact__message--error" role="alert">
                      Something went wrong. Please try again.
                    </p>
                  )}
                  <div className="bhg-landing-contact__form-actions">
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      disabled={!canSubmit}
                      loading={isSubmitting}
                    >
                      Send Message
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>

          <div className="bhg-landing-contact__map-wrapper">
            <div className="bhg-landing-contact__map-header">
              <span className="bhg-landing-contact__map-title">{ADDRESS.name}</span>
              <span className="bhg-landing-contact__map-address">
                {ADDRESS.street}, {ADDRESS.city}
              </span>
              <a
                href={MAPS_LINK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bhg-landing-contact__map-link"
              >
                Get directions
              </a>
            </div>
            <div className="bhg-landing-contact__map-container">
              <ContactMap
                fallback={
                  <div className="bhg-landing-contact__map-fallback">
                    <p className="bhg-landing-contact__map-fallback-text">
                      Set <code>VITE_GOOGLE_MAPS_EMBED_API_KEY</code> and{' '}
                      <code>VITE_GOOGLE_MAP_MAP_ID</code> in your .env to show the interactive map.
                      Enable <strong>Maps JavaScript API</strong> and <strong>Maps Embed API</strong> on
                      the key (see .env.example). Create a Map ID under Map Management in Google Cloud.
                    </p>
                    <a
                      href={MAPS_LINK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bhg-landing-contact__map-link"
                    >
                      Open in Google Maps
                    </a>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
